/* eslint-disable no-undef, eslint-comments/disable-enable-pair -- allow global variables */
// global FAAS_API_URL, ONE_IDENTITY_URL, CDAAS_API_URL -- global variables
// eslint-disable import/prefer-default-export
import cookie from 'js-cookie';
import { v4 as uuid } from 'uuid';
import styles from './timeout.scss';
import dlsStyles from './dls.css';

const langpack = require('./locale.json');

const countdownMillis = 1000 * 60; // 60 second countdown
const cushion = 1000 * 10; // timeout - 10 sec

export const CONTENT_TYPE_JSON = 'application/json; charset=UTF-8';
export const ONE_DATA_CORRELATION_ID = 'one-data-correlation-id';
export const READ_PROFILE_URL = 'ReadOneIdentityProfile.v1';

const SUCCESS = 'Success';
const CANCELED = 'Canceled';

let isAutoLogoff = false;
let checkSessionTimer = null;
let warningMillis = null;
let sessionTimer;
let sessionTimerExpire;
let warningTimer = null;
let warningTimerExpire = null;
let modal = null;
let modalContent = null;
let modalBackground = null;
let countdownTimer = null;
let countdownElement = null;
let continueSessionButton = null;
let tokenExpiry = null;
let delta = 0;
let locale = null;
let language = {};
let isModalShown = false;
let target = null;
let zIndex = 99999;
let authenticated = false;
let isDebug = false;
let debugAlias = console.debug;
let sessionFrame = null;
let frameCallback = null;
let frameAutoClose = null;
let frameAnimation = null;
let wasAuthenticated = null;
let frameResponderTypes = null;
let globalSessionResponseCode = null;
let excludeElement = {
  scroll: [],
  click: [],
  keypress: [],
};

//
// COMMON functions
//
function debug(...args) {
  if (isDebug) {
    debugAlias(...args);
  }
}

function resetVars() {
  isAutoLogoff = false;
  clearInterval(checkSessionTimer);
  warningMillis = null;
  sessionTimer = null;
  sessionTimerExpire = null;
  warningTimer = null;
  warningTimerExpire = null;
  modal = null;
  modalContent = null;
  modalBackground = null;
  countdownTimer = null;
  countdownElement = null;
  continueSessionButton = null;
  tokenExpiry = null;
  delta = 0;
  locale = null;
  language = {};
  isModalShown = false;
  target = null;
  zIndex = 99999;
  isDebug = false;
  debugAlias = console.debug;
  sessionFrame = null;
  frameCallback = null;
  frameAutoClose = null;
  frameAnimation = null;
  wasAuthenticated = null;
  frameResponderTypes = null;
  globalSessionResponseCode = null;
  excludeElement = {
    scroll: [],
    click: [],
    keypress: [],
  };
}

function setLocale(givenLocale) {
  const newLocale = givenLocale.toLowerCase();
  if (newLocale !== locale) {
    locale = langpack[newLocale] !== undefined ? newLocale : 'en-us';
    language = langpack[locale];
    isAutoLogoff = !!language.autoLogoff;
    const oldModal = modal;
    createModal();
    if (isModalShown) {
      document.body.removeChild(oldModal);
      document.body.appendChild(modal);
      modalBackground.style.opacity = 0.5;
      modalContent.style.opacity = 1;
    }
  }
}

function now() {
  return Date.now() - delta;
}

function timeSegment(segment) {
  return `0${segment}`.slice(-2);
}

function formatCountdown(countdown) {
  const minutes = Math.floor((countdown / (1000 * 60)) % 60);
  const seconds = Math.floor((countdown / 1000) % 60);
  return language.countdown
    .replace('{MIN}', minutes)
    .replace('{SEC}', timeSegment(seconds));
}

function millisToString(millis) {
  const minutes = Math.floor((millis / (1000 * 60)) % 60);
  const seconds = Math.floor((millis / 1000) % 60);

  return `${minutes}:${timeSegment(seconds)}`;
}

function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise -- allow bitwise
    const r = Math.random() * 16 | 0;
    // eslint-disable-next-line no-bitwise, no-mixed-operators -- allow bitwise
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
}

function elem(type, className, content) {
  const element = document.createElement(type);
  if (className) element.className = className;
  if (content) element.appendChild(content);
  return element;
}

function text(str) {
  return document.createTextNode(str);
}

//
// TIMEOUT related functions
//
function createModalContent() {
  const div1 = elem('div', `${styles.modal} ${dlsStyles.alertNeutral} ${dlsStyles.alertDialog}`);
  div1.ariaHidden = true;
  const spacer = elem('div', `${styles.spacer}`);
  const infoIconSvg = elem('img', `${styles.infoIconSvg}`);
  infoIconSvg.src = `${CDAAS_API_URL}/one/universal-session-manager-assets/info.filled.svg`;
  infoIconSvg.alt = 'Information Icon';
  const h1 = elem('h2', dlsStyles.marginB, text(language.heading));
  const span2 = elem('span', `${dlsStyles.dlsRed} ${dlsStyles.fontWeightBold}`);
  const button1 = elem('button', `${dlsStyles.marginT} ${dlsStyles.btn} ${dlsStyles.btnBlock} ${dlsStyles.marginAutoLr}`, text(language.continue));
  button1.ariaLabel = 'Continue session button.';
  const button2 = elem('button', `${dlsStyles.margin1T} ${dlsStyles.btn} ${dlsStyles.btnBlock} ${dlsStyles.btnTertiary} ${dlsStyles.marginAutoLr}`, text(language.logOut));
  const p1 = elem('p', dlsStyles.textAlignCenter);
  p1.appendChild(text(language.message1));
  p1.appendChild(span2);
  p1.appendChild(text(language.message2));

  button1.id = 'amexSessionExtend';
  button1.addEventListener('click', extendHandler);
  button2.id = 'amexSessionLogoff';
  button2.addEventListener('click', logoffHandler);

  div1.appendChild(infoIconSvg);
  div1.appendChild(spacer);
  div1.appendChild(h1);
  div1.appendChild(p1);
  div1.appendChild(button1);
  div1.appendChild(button2);

  countdownElement = span2;
  continueSessionButton = button1;
  return div1;
}

function createModal() {
  const container = elem('div', `${dlsStyles.module} ${styles.container}`);
  container.role = 'alert';
  container.ariaLabel = `${language.message1} 00:59 ${language.message2}`;
  const background = elem('div', styles.overlay);
  const content = elem('div', `${styles.content} ${styles.container}`, createModalContent());
  container.id = 'amexSessionTimeout';
  container.dataset.testid = 'amexSessionTimeout';
  container.appendChild(background);
  container.appendChild(content);
  modal = container;
  modalContent = content;
  modalBackground = background;
  modal.style.zIndex = zIndex;
}

function setCountdownTimer() {
  const countdownExpire = now() + countdownMillis;
  countdownTimer = setInterval(() => {
    const currentCountdown = Math.max(countdownExpire - now(), 0);
    countdownElement.innerHTML = formatCountdown(currentCountdown);
    if (currentCountdown === 0) {
      invokeLogoff(true);
    }
  }, 250);
}

function clearCountdownTimer() {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

function showWarning() {
  clearWarningTimer();
  setCountdownTimer();
  document.body.appendChild(modal);
  continueSessionButton.focus();
  isModalShown = true;
  setTimeout(() => {
    modalBackground.style.opacity = 0.5;
    modalContent.style.opacity = 1;
  }, 0);
}

function hideWarning() {
  isModalShown = false;
  modalBackground.style.opacity = 0;
  modalContent.style.opacity = 0;
  setTimeout(() => {
    document.body.removeChild(modal);
  }, 600);
}

function clearWarningTimer() {
  if (warningTimer) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }
}

function resetWarningTimer() {
  clearWarningTimer();
  if (warningMillis) {
    warningTimer = setTimeout(showWarning, warningMillis);
    warningTimerExpire = now() + warningMillis;
  }
}

function clearSessionTimer() {
  if (sessionTimer) {
    clearTimeout(sessionTimer);
    sessionTimer = null;
  }
}

export const updateUserSessionCheck = async () => {
  await checkSession(true)
    .then(() => {
      if (!authenticated && isAutoLogoff) {
        invokeLogoff(true);
      }
    });
};

function resetSessionTimer() {
  const timestamp = now();
  clearSessionTimer();
  const timerValue = tokenExpiry - timestamp - cushion;
  sessionTimer = setTimeout(updateUserSessionCheck, timerValue);
  sessionTimerExpire = tokenExpiry - cushion;
}

export function isUserAuthenticated() {
  return readUserSession()
    .then(({ isValidSession }) => isValidSession);
}

// if called by itself will return boolean for validSession, if called
// inside checkSession should return object which includes session expiry info
function readUserSession(url = `${FAAS_API_URL}/ReadUserSession.v1`) {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'one-data-correlation-id': uuidv4(),
    },
    body: JSON.stringify({}),
    credentials: 'include',
  }).then((response) => {
    globalSessionResponseCode = response.status;
    return response.json();
  })
    .then((json) => (
      {
        isValidSession: json.tokenStatus === 'VALID',
        serverTimestamp: json.serverTimestamp,
        tokenExpiry: json.tokenExpiry,
      }
    ));
}

function checkSession(update = false) {
  const url = update
    ? `${FAAS_API_URL}/UpdateUserSession.v1`
    : `${FAAS_API_URL}/ReadUserSession.v1`;

  return readUserSession(url)
    .then(({ isValidSession, serverTimestamp, tokenExpiry: sessionTokenExpiry }) => {
      if (isValidSession) {
        authenticated = true;
        if (serverTimestamp) delta = Date.now() - serverTimestamp;
        tokenExpiry = sessionTokenExpiry;
        if (update) {
          const timestamp = now();
          warningMillis = tokenExpiry - timestamp - countdownMillis;
          if (!warningTimer) {
            resetWarningTimer();
          }
        }
        resetSessionTimer();
        return true;
      }
      authenticated = false;
      return false;
    })
    .catch(() => {
      authenticated = false;
      return false;
    });
}

function updateSession() {
  return fetch(`${FAAS_API_URL}/UpdateUserSession.v1`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'one-data-correlation-id': uuidv4(),
    },
    body: JSON.stringify({}),
    credentials: 'include',
  });
}

export const pullingReadUserSession = ({ userWasAuthenticated }) => async () => {
  await checkSession()
    .then(() => {
      if (userWasAuthenticated) {
        if (globalSessionResponseCode === 400 || globalSessionResponseCode === 401) {
          invokeLogoff(true);
        }
      }
    });
};

export const setCheckSessionTimer = (callback) => {
  checkSessionTimer = setInterval(callback, 30000);
};

const refreshSession = async () => {
  const isUserAuthenticatedResult = await isUserAuthenticated();
  if (isUserAuthenticatedResult && isAutoLogoff) {
    setCheckSessionTimer(pullingReadUserSession({ userWasAuthenticated: isUserAuthenticatedResult }));
  }
  return checkSession(true);
};

function extendHandler() {
  clearCountdownTimer();
  hideWarning();
}

function invokeLogoff(auto) {
  clearCountdownTimer();
  clearSessionTimer();
  let url = `${ONE_IDENTITY_URL}/account/logout?type=${auto ? 'logoffAuto' : 'logoff'}`;
  if (target) {
    url = `${url}&destpage=${target}`;
  }
  window.location.assign(url);
}

function logoffHandler() {
  invokeLogoff(false);
}

function setZIndex(newZIndex) {
  if (typeof newZIndex === 'number') {
    // istanbul ignore else -- to be covered later
    if (modal) {
      modal.style.zIndex = newZIndex;
    }
    zIndex = newZIndex;
  }
}

function autoExtend(event) {
  if (excludeElement[event.type].indexOf(event.target) === -1) {
    resetWarningTimer();
  }
}

function addExcludeForEvent(element, type) {
  if (excludeElement[type].indexOf(element) === -1) {
    excludeElement[type].push(element);
  }
}

function exclude(element, eventList) {
  eventList.forEach((type) => {
    addExcludeForEvent(element, type);
  });
}

function setOptions(options) {
  if (options.locale) {
    setLocale(options.locale);
  }
  if (options.target) {
    ({ target } = options);
  }
  if (options.clearTarget) {
    target = null;
  }
  if (options.zIndex) {
    setZIndex(options.zIndex);
  }
}

function showStats() {
  const timestamp = now();
  debug('**** LOCALE:              ', locale);
  debug('**** TARGET:              ', target);
  debug('**** INTERNAL CLOCK:      ', new Date());
  debug('**** DELTA:               ', delta);
  debug('**** ADJUSTED CLOCK:      ', new Date(now()));
  if (sessionTimer) {
    debug('**** REMAINING IN SESSION:', millisToString(tokenExpiry - timestamp));
    debug('**** TIME UNTIL REFRESH:  ', millisToString(sessionTimerExpire - timestamp));
  }
  if (warningTimer) {
    debug('**** WARNING INTERVAL:    ', millisToString(warningMillis));
    debug('**** TIME UNTIL WARNING:  ', millisToString(warningTimerExpire - timestamp));
  }
}

function clearAllTimers() {
  clearCountdownTimer();
  clearWarningTimer();
  clearSessionTimer();
}

function removeEventListeners() {
  document.removeEventListener('click', autoExtend);
  document.removeEventListener('keypress', autoExtend);
  window.removeEventListener('scroll', autoExtend);
  document.removeEventListener('visibilitychange', visibilityHandler);
}

function removeModal() {
  if (isModalShown) {
    document.body.removeChild(modal);
    isModalShown = false;
  }
}

function clearSessionManagement() {
  clearAllTimers();
  removeEventListeners();
  removeModal();
  removeSessionFrame();
}

function resetAll() {
  clearAllTimers();
  removeEventListeners();
  removeModal();
  removeSessionFrame();
  resetVars();
}

export function checkVisibility({
  hidden,
  modalShown,
  userWasAuthenticated,
}) {
  debug('**** VISIBILITY CHANGE:', document.hidden);
  if (hidden) {
    // put the timeout functionality to sleep if tab is not active
    clearCountdownTimer();
    // istanbul ignore else -- to be covered later
    if (modalShown) {
      document.body.removeChild(modal);
      isModalShown = false;
    }
    clearWarningTimer();
    clearSessionTimer();
    wasAuthenticated = isAuthenticated();
    return updateSession();
  }
  // istanbul ignore next line
  function attempt(count = 3, delay = 1000) {
    return checkSession()
      .then((validSession) => {
        debug('**** First call time stamp =', Date.now());
        debug('**** first call valid session =', validSession);
        debug('**** before going into if statements status code =', globalSessionResponseCode);
        if (validSession) {
          resetWarningTimer();
        } else if (userWasAuthenticated) {
          if (globalSessionResponseCode === 401 || count === 0) {
            debug('****global response code value if not 200 =', globalSessionResponseCode);
            invokeLogoff();
          } else {
            setTimeout(() => {
              debug('**** Second call time stamp =', Date.now());
              debug('**** valid session value on subsequent attempt =', validSession);
              attempt(count - 1);
            }, delay);
          }
        }
      });
  }

  return attempt(1);
}

// istanbul ignore next -- to be covered later
function visibilityHandler() {
  return checkVisibility({
    hidden: document.hidden,
    modalShown: isModalShown,
    userWasAuthenticated: wasAuthenticated,
  });
}

export function initialize() {
  resetAll();

  const userLocale = cookie.get('axplocale') || window.navigator.language || /* istanbul ignore next -- to be covered later */ 'en-US';
  setLocale(userLocale);
  createModal();

  // set up document event handlers
  document.addEventListener('click', autoExtend, true);
  document.addEventListener('keypress', autoExtend, true);
  window.addEventListener('scroll', autoExtend, true);
  document.addEventListener('visibilitychange', visibilityHandler, false);

  return refreshSession();
}

function isAuthenticated() {
  return authenticated;
}

//
// AUTHENTICATION related functions
//

// istanbul ignore next -- to be covered later
function frameLoad() {
  sessionFrame.style.opacity = 1;
}

const createFrame = ({
  id,
  src,
}) => {
  const frame = elem('iframe');
  frame.id = id;
  frame.src = src;
  frame.style.minHeight = '100vh';
  frame.style.minWidth = '100vw';
  frame.style.position = 'fixed';
  frame.style.top = 0;
  frame.style.zIndex = zIndex;
  frame.style.border = 0;
  if (frameAnimation) {
    frame.style.opacity = 0;
    frame.style.transition = 'opacity 400ms';
    frame.addEventListener('load', frameLoad);
  }
  return frame;
};

export const getStatusCode = (status, incomingStatusCode) => {
  let statusCode = status;
  if (incomingStatusCode !== undefined) {
    statusCode = incomingStatusCode;
  }
  if (status === SUCCESS) statusCode = 0;
  if (status === CANCELED) statusCode = 101;
  if (status === 1) statusCode = 0;
  return statusCode;
};

export const messageResponder = ({
  data: {
    messageId, status, statusCode: incomingStatusCode, errorCode, target: targetURL, ...other
  },
}) => {
  if (frameResponderTypes.includes(messageId)) {
    debug('**** MESSAGE =', messageId, status, errorCode, targetURL);
    window.removeEventListener('message', messageResponder);

    // istanbul ignore else -- to be covered later
    if (messageId === 'LOGIN_NAV') {
      closeSessionFrame();
      window.location.assign(targetURL);
    } else {
      // istanbul ignore else -- to be covered later
      if (frameAutoClose) closeSessionFrame();
      const statusCode = getStatusCode(status, incomingStatusCode);
      if (frameCallback) {
        frameCallback({
          status, statusCode, errorCode, ...other,
        });
      }
      if (messageId === 'LOGIN' && statusCode === 0) {
        refreshSession();
      }
    }
  }
};

const cleanUp = () => {
  window.addEventListener('message', messageResponder, false);
  window.addEventListener('popstate', removeSessionFrame);
  document.body.appendChild(sessionFrame);
  return true;
};

const createSessionFrame = (url, id) => {
  sessionFrame = createFrame({
    id,
    src: url,
  });
  return cleanUp();
};

const setGlobalVariables = ({ useAnimation, callback, autoClose }) => {
  frameAnimation = useAnimation;
  frameCallback = callback;
  frameAutoClose = autoClose;
  return true;
};

// Login Frame
const login = ({
  autoClose = false,
  callback,
  style,
  url,
}) => {
  if (sessionFrame) {
    return false;
  }
  frameResponderTypes = ['LOGIN', 'LOGIN_NAV'];
  setGlobalVariables({ callback, autoClose });
  return createSessionFrame(`${url || ONE_IDENTITY_URL}/${locale.toLowerCase()}/account/${style === 'light' ? 'light' : 'compact'}/login?usm=yes`, 'amexSessionLogin');
};

const framePreFetch = (url) => fetch(`${FAAS_API_URL}/${url}`, {
  method: 'POST',
  headers: {
    'Content-Type': CONTENT_TYPE_JSON,
    [ONE_DATA_CORRELATION_ID]: uuid(),
  },
  credentials: 'include',
  timeout: 9e3,
  body: JSON.stringify({}),
}).then(({ status }) => status);

// Profile Frame
const buildProfile = ({
  autoClose = false,
  callback,
  showErrors = false,
  preFetch = false,
  url,
}) => {
  if (sessionFrame) {
    return false;
  }
  frameResponderTypes = ['PROFILE'];
  setGlobalVariables({ callback, autoClose });
  if (preFetch) {
    return framePreFetch(READ_PROFILE_URL).then((status) => {
      if (status === 200) {
        return frameCallback({ statusCode: 0 });
      }
      return createSessionFrame(`${url || ONE_IDENTITY_URL}/${locale.toLowerCase()}/account/profile/build${showErrors
        ? '' : '?errors=off'}`, 'amexSessionProfile');
    });
  }
  return createSessionFrame(`${url || ONE_IDENTITY_URL}/${locale.toLowerCase()}/account/profile/build${showErrors
    ? '' : '?errors=off'}`, 'amexSessionProfile');
};

const setIntervalX = (postMessage, delay, repetitions) => {
  let count = 0;
  const intervalID = setInterval(() => {
    count += 1;
    postMessage();
    if (count === repetitions) {
      clearInterval(intervalID);
    }
  }, delay);
};

// Instant Profile iFrame
const instaProfile = ({
  autoClose = false,
  callback,
  url,
  givenName,
  familyName,
  email,
  phone,
  countryCode,
  source,
  journeyId,
  domainId,
  config,
}) => {
  if (sessionFrame) {
    return false;
  }
  frameResponderTypes = ['INSTA_PROFILE'];
  setGlobalVariables({ callback, autoClose });
  createSessionFrame(`${url || ONE_IDENTITY_URL}/${locale.toLowerCase()}/account/profile/make`, 'amexSessionInstantProfile');
  const iFrameWindow = document.querySelector('#amexSessionInstantProfile');

  return setIntervalX(() => {
    iFrameWindow.contentWindow.postMessage({
      messageId: 'INSTA_PROFILE',
      userInfo: {
        givenName,
        familyName,
        email,
        phone,
        countryCode,
        source,
        journeyId,
        domainId,
      },
      config,
    }, '*');
  }, 2000, 8);
};

// Profile Refresh iFrame
const profileRefresh = ({
  autoClose = false,
  callback,
  url,
  source,
  refresh,
  add,
  hideSuccessPage,
}) => {
  if (sessionFrame) {
    return false;
  }
  frameResponderTypes = ['PROFILE_REFRESH'];
  setGlobalVariables({ callback, autoClose });
  createSessionFrame(`${url || ONE_IDENTITY_URL}/${locale.toLowerCase()}/account/profile/refresh`, 'amexSessionProfileRefresh');
  const iFrameWindow = document.querySelector('#amexSessionProfileRefresh');

  return setIntervalX(() => {
    iFrameWindow.contentWindow.postMessage({
      messageId: 'PROFILE_REFRESH',
      config: {
        refresh,
        add,
        source,
        hideSuccessPage,
      },
    }, '*');
  }, 2000, 8);
};

function getProfileProps({
  isProfileInfoPageHidden,
  isGovIdPageHidden,
  isLoginDetailsPageHidden,
  isSuccessPageHidden,
}) {
  let queryParam = '';

  queryParam = '?skipSteps=createProfileOrLogin';
  queryParam += isProfileInfoPageHidden ? ',profileInformation' : '';
  queryParam += isGovIdPageHidden ? ',governmentIdentity' : '';
  queryParam += isLoginDetailsPageHidden ? ',loginDetails' : '';
  queryParam += isSuccessPageHidden ? ',success' : '';

  return queryParam;
}

// Create Profile frame
const createProfileWithCredentials = ({
  autoClose = false,
  callback,
  url,
  hiddenFields = {},
  profileInformation = {},
  fullFlow = false,
  isProfileInfoPageHidden = false,
  isGovIdPageHidden = true,
  isLoginDetailsPageHidden = false,
  isSuccessPageHidden = false,
}) => {
  if (sessionFrame) {
    return false;
  }

  let queryParam = '';

  if (!fullFlow) {
    queryParam = getProfileProps({
      isProfileInfoPageHidden,
      isGovIdPageHidden,
      isLoginDetailsPageHidden,
      isSuccessPageHidden,
    });
  }

  frameResponderTypes = ['CREATE_PROFILE_WITH_CREDENTIALS'];
  setGlobalVariables({ callback, autoClose });
  createSessionFrame(`${url || ONE_IDENTITY_URL}/${locale.toLowerCase()}/account/profile/create${queryParam}`, 'amexSessionCreateProfileWithCredentials');
  return setIntervalX(() => {
    document.querySelector('#amexSessionCreateProfileWithCredentials').contentWindow.postMessage({
      messageId: 'CREATE_PROFILE_WITH_CREDENTIALS',
      hiddenFields,
      profileInformation,
      isEmailCollisionAllowed: false,
    }, '*');
  }, 2000, 8);
};

const createProfile = ({
  autoClose = false,
  callback,
  url,
  hiddenFields = {},
  profileSource = '',
  profileInformation = {},
  isProfileInfoPageHidden = false,
  isGovIdPageHidden = true,
  isLoginDetailsPageHidden = false,
  isSuccessPageHidden = false,
}) => {
  if (sessionFrame) {
    return false;
  }

  const queryParam = getProfileProps({
    isProfileInfoPageHidden,
    isGovIdPageHidden,
    isLoginDetailsPageHidden,
    isSuccessPageHidden,
  });

  frameResponderTypes = ['CREATE_PROFILE_WITH_CREDENTIALS'];
  setGlobalVariables({ callback, autoClose });
  createSessionFrame(`${url || ONE_IDENTITY_URL}/${locale.toLowerCase()}/account/profile/create${queryParam}`, 'amexSessionCreateProfileWithCredentials');
  return setIntervalX(() => {
    document.querySelector('#amexSessionCreateProfileWithCredentials').contentWindow.postMessage({
      messageId: 'CREATE_PROFILE_WITH_CREDENTIALS',
      hiddenFields,
      profileSource,
      profileInformation,
      isEmailCollisionAllowed: true,
    }, '*');
  }, 2000, 8);
};

// Identity Management Center frame
const identityManagementCenter = ({
  autoClose = false,
  callback,
  url,
  hiddenSections = {},
}) => {
  if (sessionFrame) {
    return false;
  }
  frameResponderTypes = ['IDENTITY_MANAGEMENT_CENTER'];
  setGlobalVariables({ callback, autoClose });
  createSessionFrame(`${url || ONE_IDENTITY_URL}/${locale.toLowerCase()}/account/profile/manage`, 'amexSessionIdentityManagementCenter');
  return setIntervalX(() => {
    document.querySelector('#amexSessionIdentityManagementCenter').contentWindow.postMessage({
      messageId: 'IDENTITY_MANAGEMENT_CENTER',
      hiddenSections,
    }, '*');
  }, 2000, 8);
};

export function getAuthProps({
  userJourneyIdentifier,
  state,
  actionId,
  applicationId,
  accountKey,
  factName,
  factValue,
  showErrors = true,
  showPlaceholder = true,
  useAnimation = true,
}) {
  return {
    ...userJourneyIdentifier && { userJourneyIdentifier: encodeURIComponent(userJourneyIdentifier) },
    ...state && { state: encodeURIComponent(state) },
    ...actionId && { actionId },
    ...applicationId && { applicationId },
    ...accountKey && { accountKey },
    ...factName && factValue && { factName, factValue },
    ...showErrors && { errors: 'on' },
    ...showPlaceholder && { loader: 'placeholder' },
    ...useAnimation === false && { animation: 'off' },
  };
}

const authenticate = (props) => {
  const {
    useAnimation = true,
    callback,
    autoClose = false,
    url,
  } = props;

  if (sessionFrame) {
    return false;
  }
  frameResponderTypes = ['AUTHN'];
  setGlobalVariables({ useAnimation, callback, autoClose });
  const authProps = getAuthProps(props);
  const params = Object.keys(authProps)
    .map((key) => `${key}=${authProps[key]}`)
    .join('&');
  return createSessionFrame(`${url || ONE_IDENTITY_URL}/${locale.toLowerCase()}/account/authenticate?${params}`, 'amexSessionAuthenticate');
};

function removeSessionFrame() {
  if (sessionFrame) {
    document.body.removeChild(sessionFrame);
    window.removeEventListener('message', messageResponder);
    window.removeEventListener('popstate', removeSessionFrame);
    sessionFrame = null;
  }
}

// istanbul ignore next -- to be covered later
function closeSessionFrame() {
  if (sessionFrame) {
    if (frameAnimation) {
      sessionFrame.style.opacity = 0;
      setTimeout(() => {
        removeSessionFrame();
      }, 400);
    } else {
      removeSessionFrame();
    }
  }
}

export function isFramed() {
  return !!sessionFrame;
}

export function admin({ operation }) {
  if (operation === 1) showWarning();
}

//
// GLOBALS
//
window.AmexSession = {
  info: () => ({
    warningMillis,
    warningTimer,
    warningTimerExpire,
    sessionTimer,
    sessionTimerExpire,
    tokenExpiry,
    locale,
    target,
    delta,
    countdownTimer,
    language,
    isModalShown,
    zIndex,
    authenticated,
    isDebug,
    excludeElement,
  }),
  admin,
  stats: showStats,
  start: refreshSession,
  clearSessionManagement,
  resetAll,
  options: setOptions,
  exclude,
  extend: resetWarningTimer,
  debug: (value) => { isDebug = value; },
  isAuthenticated,
  authenticate,
  isUserAuthenticated,
  closeSessionFrame,
  closeAuthentication: closeSessionFrame,
  login,
  buildProfile,
  instaProfile,
  profileRefresh,
  createProfileWithCredentials,
  createProfile,
  identityManagementCenter,
  isFramed,
};

initialize();
